import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    BoltIcon,
    LockIcon,
    EyeIcon,
    EyeSlashIcon,
    ArrowLeftIcon,
    CheckIcon
} from '@patternfly/react-icons';
import {
    Progress,
    ProgressSize,
    ProgressMeasureLocation,
    ProgressVariant
} from '@patternfly/react-core';
import { resetPassword } from '@app/utils/utils';
import { BorderedInput } from '@app/Login/BorderedInput';

const PURPLE_GRADIENT = 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)';

const ResetPasswordPage: React.FunctionComponent = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const getPasswordStrength = (pwd: string): { strength: number; label: string; variant: ProgressVariant; color: string } => {
        if (!pwd) return { strength: 0, label: '', variant: ProgressVariant.danger, color: '#dc2626' };

        let strength = 0;
        if (pwd.length >= 8) strength += 25;
        if (pwd.length >= 12) strength += 15;
        if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 20;
        if (/\d/.test(pwd)) strength += 20;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) strength += 20;

        if (strength < 40) return { strength, label: 'Weak', variant: ProgressVariant.danger, color: '#dc2626' };
        if (strength < 70) return { strength, label: 'Fair', variant: ProgressVariant.warning, color: '#f59e0b' };
        return { strength, label: 'Strong', variant: ProgressVariant.success, color: '#059669' };
    };

    const passwordStrength = getPasswordStrength(newPassword);
    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
    const isFormValid = newPassword && confirmPassword && passwordsMatch && passwordStrength.strength >= 40;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid || !token) return;

        setIsLoading(true);
        setError('');
        try {
            const response = await resetPassword(token, newPassword);
            if (response.ok) {
                setSuccess(true);
            } else {
                setError('Invalid or expired reset link. Please request a new one.');
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const styles = {
        page: {
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
            padding: '24px'
        },
        card: {
            background: '#ffffff',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '460px',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)',
            padding: '40px 40px 32px'
        },
        logoBadge: {
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)'
        },
        title: {
            color: '#0f172a',
            fontSize: '28px',
            fontWeight: 700,
            margin: 0,
            marginBottom: '8px',
            textAlign: 'center' as const,
            letterSpacing: '-0.5px'
        },
        subtitle: {
            color: '#6b7280',
            fontSize: '15px',
            margin: 0,
            marginBottom: '28px',
            textAlign: 'center' as const,
            lineHeight: 1.5
        },
        fieldLabel: {
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '8px'
        },
        fieldGroup: {
            marginBottom: '16px'
        },
        strengthContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '10px'
        },
        strengthLabel: {
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px'
        },
        button: {
            width: '100%',
            background: PURPLE_GRADIENT,
            color: '#ffffff',
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
            cursor: 'pointer',
            marginTop: '8px'
        },
        buttonDisabled: {
            width: '100%',
            background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: 'none',
            cursor: 'not-allowed',
            marginTop: '8px'
        },
        errorBox: {
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#dc2626',
            fontSize: '14px'
        },
        backLinkRow: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px'
        },
        backLink: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#6366f1',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '14px'
        },
        successIconWrap: {
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            color: '#059669',
            fontSize: '32px'
        },
        eyeButton: {
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center'
        } as React.CSSProperties
    };

    if (!token) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.logoBadge}>
                        <BoltIcon style={{ fontSize: '28px', color: '#fbbf24' }} />
                    </div>
                    <h1 style={styles.title}>Invalid link</h1>
                    <p style={styles.subtitle}>
                        The password reset link is missing or invalid. Please request a new one.
                    </p>
                    <a href="/forgot-password" style={{ ...styles.button, display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' as const }}>
                        Request new reset link
                    </a>
                    <div style={styles.backLinkRow}>
                        <a href="/" style={styles.backLink}>
                            <ArrowLeftIcon /> Back to login
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.successIconWrap}>
                        <CheckIcon />
                    </div>
                    <h1 style={styles.title}>Password reset!</h1>
                    <p style={styles.subtitle}>
                        Your password has been updated. You can now sign in with your new password.
                    </p>
                    <a href="/" style={{ ...styles.button, display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' as const }}>
                        Go to login
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.logoBadge}>
                    <BoltIcon style={{ fontSize: '28px', color: '#fbbf24' }} />
                </div>
                <h1 style={styles.title}>Reset password</h1>
                <p style={styles.subtitle}>Choose a new password for your account.</p>

                {error && <div style={styles.errorBox}>{error}</div>}

                <form onSubmit={handleSubmit} style={{ border: 'none', padding: 0, background: 'transparent', margin: 0, display: 'block', maxWidth: 'none' }}>
                    <div style={styles.fieldGroup}>
                        <label style={styles.fieldLabel} htmlFor="new-password">New Password</label>
                        <BorderedInput
                            id="new-password"
                            value={newPassword}
                            onChange={setNewPassword}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Create a strong password"
                            icon={<LockIcon />}
                            autoComplete="new-password"
                            rightAdornment={
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    style={styles.eyeButton}
                                >
                                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                </button>
                            }
                        />
                        {newPassword && (
                            <div style={styles.strengthContainer}>
                                <div style={{ flex: 1 }}>
                                    <Progress
                                        value={passwordStrength.strength}
                                        size={ProgressSize.sm}
                                        measureLocation={ProgressMeasureLocation.none}
                                        variant={passwordStrength.variant}
                                        aria-label="Password strength"
                                    />
                                </div>
                                <span style={{ ...styles.strengthLabel, color: passwordStrength.color }}>
                                    {passwordStrength.label}
                                </span>
                            </div>
                        )}
                    </div>

                    <div style={styles.fieldGroup}>
                        <label style={styles.fieldLabel} htmlFor="confirm-password">Confirm Password</label>
                        <BorderedInput
                            id="confirm-password"
                            value={confirmPassword}
                            onChange={setConfirmPassword}
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Re-enter your password"
                            icon={<LockIcon />}
                            autoComplete="new-password"
                            hasError={!!(confirmPassword && !passwordsMatch)}
                            rightAdornment={
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword((s) => !s)}
                                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                    style={styles.eyeButton}
                                >
                                    {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                </button>
                            }
                        />
                        {confirmPassword && !passwordsMatch && (
                            <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '6px' }}>
                                Passwords do not match
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={!isFormValid || isLoading}
                        style={!isFormValid || isLoading ? styles.buttonDisabled : styles.button}
                    >
                        {isLoading ? 'Resetting...' : 'Reset password'}
                    </button>
                    <div style={styles.backLinkRow}>
                        <a href="/" style={styles.backLink}>
                            <ArrowLeftIcon /> Back to login
                        </a>
                    </div>
                </form>
            </div>
        </div>
    );
};

export { ResetPasswordPage };
